import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Sparkles, Send, Loader2, ArrowLeft, CheckCircle2, CalendarClock, MapPin, Users, DollarSign, Paperclip, UserCheck } from 'lucide-react';
import { VoiceInput } from '../../components/VoiceInput';
import api from '../../utils/api';
import { toast } from 'sonner';

const Bubble = ({ role, text }) => (
  <div className={`flex ${role === 'user' ? 'justify-end' : 'justify-start'}`}>
    <div
      data-testid={`evie-msg-${role}`}
      className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-line break-words [overflow-wrap:anywhere] ${
        role === 'user'
          ? 'bg-violet-600 text-white rounded-br-sm'
          : 'bg-white border border-violet-100 text-gray-800 rounded-bl-sm shadow-sm'
      }`}
    >
      {text}
    </div>
  </div>
);

const DraftSummary = ({ draft }) => {
  const extras = [
    ['event_type', 'Event'], ['purpose', 'Purpose'], ['dress_code', 'Dress code'],
    ['wardrobe_notes', 'Wardrobe'], ['scenes_scripts', 'Scenes/scripts'],
    ['deliverables', 'Deliverables'], ['special_instructions', 'Notes'],
  ].filter(([k]) => draft[k]);
  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-3 text-sm space-y-1.5" data-testid="evie-draft-summary">
      {draft.avatar_name && (
        <div className="flex items-center gap-2 pb-1.5 mb-1 border-b border-violet-200" data-testid="evie-draft-avatar">
          {draft.avatar_url
            ? <img src={draft.avatar_url} alt={draft.avatar_name} className="w-8 h-8 rounded-full object-cover" />
            : <Sparkles className="w-4 h-4 text-fuchsia-500" />}
          <span className="font-semibold text-fuchsia-700">{draft.avatar_name}</span>
        </div>
      )}
      <div className="flex items-center gap-2 text-violet-800 font-semibold"><Users className="w-4 h-4" /> {draft.role_name} × {draft.talent_needed}</div>
      {draft.direct_talent_display && (
        <div className="flex items-center gap-2 text-emerald-700 font-semibold" data-testid="evie-draft-direct-talent">
          <UserCheck className="w-4 h-4" /> Direct booking: {draft.direct_talent_display}
        </div>
      )}
      {draft.recurrence_dates && draft.recurrence_dates.length >= 2 ? (
        <div className="text-gray-700" data-testid="evie-draft-recurring">
          <div className="flex items-center gap-2 font-semibold text-violet-800">
            <CalendarClock className="w-4 h-4 text-violet-500" /> Recurring: {draft.recurrence_dates.length} sessions ({draft.recurrence_freq})
          </div>
          <div className="ml-6 text-xs text-gray-600">
            {draft.recurrence_dates.slice(0, 4).join(', ')}{draft.recurrence_dates.length > 4 ? ', …' : ''} · {draft.start_time}–{draft.end_time}
          </div>
        </div>
      ) : draft.is_multi_day && draft.end_date ? (
        <div className="text-gray-700" data-testid="evie-draft-multiday">
          <div className="flex items-center gap-2 font-semibold text-violet-800">
            <CalendarClock className="w-4 h-4 text-violet-500" /> Multi-day: {draft.shift_date} → {draft.end_date}
          </div>
          {draft.customize_per_day && Array.isArray(draft.per_day_schedules) ? (
            <div className="ml-6 mt-0.5 space-y-0.5">
              {draft.per_day_schedules.map((e, i) => (
                <div key={i} className="text-xs text-gray-600">{e.date}: {e.start_time && e.end_time ? `${e.start_time}–${e.end_time}` : 'hours TBD'}</div>
              ))}
            </div>
          ) : (
            <div className="ml-6 text-xs text-gray-600">{draft.start_time}–{draft.end_time} each day</div>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-gray-700"><CalendarClock className="w-4 h-4 text-violet-500" /> {draft.shift_date} · {draft.start_time}–{draft.end_time}</div>
      )}
      <div className="flex items-center gap-2 text-gray-700"><MapPin className="w-4 h-4 text-violet-500" /> {draft.venue_name ? `${draft.venue_name}, ` : ''}{draft.venue_address}, {draft.city}, {draft.state}</div>
      <div className="flex items-center gap-2 text-gray-700"><DollarSign className="w-4 h-4 text-violet-500" /> ${draft.bill_rate}/hr</div>
      {extras.length > 0 && (
        <div className="pt-1.5 mt-1 border-t border-violet-200 space-y-0.5">
          {extras.map(([k, label]) => (
            <div key={k} className="text-xs text-gray-600"><span className="font-medium text-gray-700">{label}:</span> {draft[k]}</div>
          ))}
        </div>
      )}
      {Array.isArray(draft.materials_links) && draft.materials_links.length > 0 && (
        <div className="text-xs text-gray-600 pt-1"><span className="font-medium text-gray-700">Links:</span> {draft.materials_links.join(', ')}</div>
      )}
      {Array.isArray(draft.uploaded_files) && draft.uploaded_files.length > 0 && (
        <div className="text-xs text-gray-600"><span className="font-medium text-gray-700">Files:</span> {draft.uploaded_files.map(f => f.name).join(', ')}</div>
      )}
      {Array.isArray(draft.pending_items) && draft.pending_items.length > 0 && (
        <div className="mt-1.5 pt-1.5 border-t border-amber-200 text-xs text-amber-700" data-testid="evie-pending-items">
          <span className="font-semibold">⏳ Still outstanding:</span> {draft.pending_items.join(', ')}
        </div>
      )}
    </div>
  );
};

const EvieBooking = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    { role: 'evie', text: "Hi, I'm Evie 👋 Tell me about your event and I'll set up the booking. For example: \"3 brand ambassadors in Atlanta on Dec 20, 10am–6pm at 500 Marietta St, $32/hr.\"" },
  ]);
  const [input, setInput] = useState('');
  const [draft, setDraft] = useState({});
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/evie/booking/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDraft(d => ({ ...d, uploaded_files: [...(d.uploaded_files || []), { name: r.data.name, url: r.data.url }] }));
      setMessages(m => [...m, { role: 'evie', text: `Got your file "${r.data.name}" — I've attached it to the event.` }]);
      toast.success('File attached');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    // Capture the last thing Evie asked + recent transcript so the backend can
    // attribute this answer to the right field and never re-ask it.
    const lastQuestion = [...messages].reverse().find(m => m.role === 'evie')?.text || '';
    const history = messages.slice(-10).map(m => ({ role: m.role === 'user' ? 'user' : 'evie', text: m.text }));
    setMessages(m => [...m, { role: 'user', text: msg }]);
    setInput('');
    setLoading(true);
    try {
      const r = await api.post('/evie/booking/parse', { message: msg, draft, history, last_question: lastQuestion });
      setDraft(r.data.draft || {});
      setReady(!!r.data.ready);
      setMessages(m => [...m, { role: 'evie', text: r.data.assistant_message || 'Got it.', draft: r.data.ready ? r.data.draft : null }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'evie', text: "Sorry, I couldn't process that. Could you rephrase?" }]);
    } finally { setLoading(false); }
  };

  const confirm = async () => {
    if (confirming) return;
    setConfirming(true);
    try {
      const r = await api.post('/evie/booking/confirm', { draft });
      const shift = r.data.shift || {};
      const da = r.data.direct_assignment;
      toast.success('Booking created!');
      let text;
      if (r.data.recurring) {
        text = `✅ Done! I've created ${r.data.occurrences} recurring sessions and linked them as one series.`;
      } else {
        const pending = shift.rate_approval_status === 'pending_admin_approval';
        text = pending
          ? "✅ Your booking is submitted! Your rate is below our standard minimum, so it's pending admin approval — we'll confirm pricing shortly."
          : "✅ All set! Your booking has been created and is now live.";
      }
      if (da && da.ok) {
        const who = da.talent_name || 'The talent';
        text += r.data.recurring
          ? `\n\n👤 ${who} has been booked onto ${da.count || r.data.occurrences} of the sessions and notified — awaiting their confirmation.`
          : `\n\n👤 ${who} has been booked directly onto this gig and notified — awaiting their confirmation.`;
      } else if (da && !da.ok) {
        text += `\n\n⚠️ The gig was created, but I couldn't auto-assign ${da.talent_name || 'the talent'}: ${da.error}. You can assign them from Operations.`;
      }
      setMessages(m => [...m, { role: 'evie', text }]);
      setReady(false);
      setTimeout(() => navigate(shift.id ? `/client/shifts/${shift.id}` : '/client/shifts'), 1800);    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create the booking');
    } finally { setConfirming(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4" data-testid="evie-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-md">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">Book with Evie</h1>
            <p className="text-sm text-gray-500">Create an event just by chatting — no forms needed</p>
          </div>
        </div>

        <Card className="mt-4 border-violet-100">
          <CardContent className="p-4">
            <div className="space-y-3 max-h-[52vh] overflow-y-auto pr-1" data-testid="evie-thread">
              {messages.map((m, i) => (
                <div key={i} className="space-y-2">
                  <Bubble role={m.role} text={m.text} />
                  {m.draft && <DraftSummary draft={m.draft} />}
                </div>
              ))}
              {loading && (
                <div className="flex justify-start"><div className="bg-white border border-violet-100 rounded-2xl px-4 py-2.5 shadow-sm"><Loader2 className="w-4 h-4 animate-spin text-violet-500" /></div></div>
              )}
              <div ref={endRef} />
            </div>

            {ready && (
              <Button onClick={confirm} disabled={confirming} className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="evie-confirm-booking">
                {confirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Confirm &amp; Create Booking
              </Button>
            )}

            <div className="flex items-center gap-2 mt-3 flex-wrap sm:flex-nowrap">
              <VoiceInput onTranscript={(t) => setInput(t)} disabled={loading} />
              <input ref={fileRef} type="file" onChange={uploadFile} className="hidden"
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" data-testid="evie-file-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || loading}
                title="Attach a document (floor plan, script, menu…)"
                className="shrink-0 inline-flex items-center justify-center rounded-md h-10 w-10 bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors disabled:opacity-50"
                data-testid="evie-upload-btn">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
              </button>
              <Input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && send()}
                placeholder="Tell Evie what you need…"
                disabled={loading}
                className="flex-1 min-w-0"
                data-testid="evie-input"
              />
              <Button onClick={send} disabled={loading || !input.trim()} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="evie-send">
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">You can also <button onClick={() => navigate('/client/shifts/create')} className="underline hover:text-violet-600">create a booking manually</button>.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EvieBooking;
